import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from './AuthContext';
import PageHeaderActionsBar from './ui/PageHeaderActionsBar';
import {
  Typography,
  Grid,
  Box,
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Card,
  CardContent,
  Alert,
  IconButton,
  Skeleton,
  Menu,
  MenuItem
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  AccountBalanceWallet as CreditIcon,
  TrendingUp as TrendingUpIcon,
  AttachMoney as AttachMoneyIcon,
  Receipt as ReceiptIcon,
  MoreVert as MoreVertIcon,
  AssignmentTurnedIn as ApplyIcon
} from '@mui/icons-material';
import api from '../api';

const BillingCredits = () => {
  const navigate = useNavigate();
  const { userPermissions } = useContext(AuthContext) || {};
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [credits, setCredits] = useState([]);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuCredit, setMenuCredit] = useState(null);

  const fetchCredits = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/api/billing/credits/');
      const data = response.data;
      if (data?.credits) {
        setCredits(data.credits);
      } else {
        setCredits([]);
      }
    } catch (err) {
      console.error('Error fetching credits:', err);
      setError('Failed to load credits');
      setCredits([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userPermissions && Object.keys(userPermissions).length > 0 && !userPermissions.can_access_credits) {
      setError('No access to credits features.');
      setLoading(false);
      return;
    }
    fetchCredits();
  }, [userPermissions]);

  const handleCreateCredit = () => {
    navigate('/billing/credits/create');
  };

  const handleMenuOpen = (e, credit) => {
    e.stopPropagation();
    setMenuAnchor(e.currentTarget);
    setMenuCredit(credit);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setMenuCredit(null);
  };

  const handleDeleteCredit = async () => {
    if (!menuCredit) return;
    if (!window.confirm('Are you sure you want to delete this credit?')) {
      handleMenuClose();
      return;
    }
    try {
      await api.delete(`/api/billing/credits/${menuCredit.id}/`);
      setCredits(credits.filter(c => c.id !== menuCredit.id));
    } catch (err) {
      console.error('Error deleting credit:', err);
      setError('Failed to delete credit');
    }
    handleMenuClose();
  };

  const handleApplyToInvoice = () => {
    if (menuCredit) {
      navigate(`/billing/credits/${menuCredit.id}/apply`);
    }
    handleMenuClose();
  };

  const getStatusColor = (status) => {
    switch ((status || '').toLowerCase()) {
      case 'open': return 'success';
      case 'applied': return 'info';
      case 'closed': return 'default';
      default: return 'default';
    }
  };

  const totalCredits = credits.reduce((sum, c) => sum + (parseFloat(c.amount) || 0), 0);
  const totalRemaining = credits.reduce((sum, c) => sum + (parseFloat(c.credit_remaining) || 0), 0);
  const openCount = credits.filter(c => (c.status || 'open').toLowerCase() === 'open').length;

  const StatCard = ({ icon: Icon, label, value, color, accentColor }) => (
    <Card
      sx={{
        height: '100%',
        borderRadius: 2,
        boxShadow: 0,
        border: '1px solid',
        borderColor: 'divider',
        borderLeft: '4px solid',
        borderLeftColor: accentColor || 'primary.main',
        transition: 'box-shadow 0.2s ease',
        '&:hover': { boxShadow: 2 }
      }}
    >
      <CardContent sx={{ py: 2.5, px: 2.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: 2,
              bgcolor: (color === 'primary' && 'primary.50') || (color === 'success' && 'success.50') || 'grey.100',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: color ? `${color}.main` : 'primary.main'
            }}
          >
            <Icon sx={{ fontSize: 24 }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              {label}
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 700, mt: 0.25 }} color={color ? `${color}.main` : 'text.primary'}>
              {value}
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  if (error && !userPermissions?.can_access_credits) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (loading) {
    return (
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Box>
              <Skeleton variant="text" width={220} height={40} />
              <Skeleton variant="text" width={420} height={24} sx={{ mt: 0.5 }} />
            </Box>
          </Box>
          <Grid container spacing={3} sx={{ mb: 3 }}>
            {[1, 2, 3].map((i) => (
              <Grid item xs={12} sm={4} key={i}>
                <Skeleton variant="rounded" height={100} sx={{ borderRadius: 2 }} />
              </Grid>
            ))}
          </Grid>
          <Skeleton variant="rounded" height={320} sx={{ borderRadius: 2 }} />
        </Box>
    );
  }

  return (
    <Box>
      <PageHeaderActionsBar
        title="Client Credits"
        description="Manage prepayments, overpayments, and credit memos that can be applied to future invoices"
        primaryAction={{ label: 'Create Credit', onClick: handleCreateCredit, icon: <AddIcon />, disabled: !userPermissions?.can_access_credits }}
      />

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Stats */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={4}>
            <StatCard
              icon={TrendingUpIcon}
              label="Total Credits"
              value={`$${totalCredits.toLocaleString('en-US', { minimumFractionDigits: 2 })}`}
              color="primary"
              accentColor="primary.main"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <StatCard
              icon={AttachMoneyIcon}
              label="Available Balance"
              value={`$${totalRemaining.toLocaleString('en-US', { minimumFractionDigits: 2 })}`}
              color="success"
              accentColor="success.main"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <StatCard
              icon={ReceiptIcon}
              label="Open Credits"
              value={openCount}
              color="info"
              accentColor="info.main"
            />
          </Grid>
        </Grid>

        {/* Credits Table */}
        <Paper
          sx={{
            borderRadius: 2,
            boxShadow: 0,
            border: '1px solid',
            borderColor: 'divider',
            overflow: 'hidden'
          }}
        >
          <Box sx={{ px: 2.5, py: 2, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              All Credits
            </Typography>
          </Box>
          {credits.length > 0 ? (
            <TableContainer>
              <Table size="medium">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>Credit #</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>Client</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>Description</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>Amount</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>Remaining</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>Date</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>Status</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.75rem', width: 60 }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {credits.map((credit) => (
                    <TableRow
                      key={credit.id}
                      hover
                      sx={{ '&:hover': { bgcolor: 'action.hover' } }}
                    >
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {credit.credit_number}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{credit.client_name}</Typography>
                      </TableCell>
                      <TableCell sx={{ maxWidth: 220 }}>
                        <Typography variant="body2" noWrap title={credit.description} color="text.secondary">
                          {credit.description || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight={500}>
                          ${(parseFloat(credit.amount) || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight={600} color="success.main">
                          ${(parseFloat(credit.credit_remaining) || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {credit.date || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={credit.status || 'Open'}
                          color={getStatusColor(credit.status)}
                          size="small"
                          sx={{ fontWeight: 500 }}
                        />
                      </TableCell>
                      <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                        <IconButton
                          size="small"
                          onClick={(e) => handleMenuOpen(e, credit)}
                          sx={{ color: 'text.secondary' }}
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Box sx={{ py: 10, px: 3, textAlign: 'center' }}>
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  bgcolor: 'grey.200',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 2
                }}
              >
                <CreditIcon sx={{ fontSize: 40, color: 'grey.500' }} />
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                No credits yet
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 400, mx: 'auto' }}>
                Credits are used when clients overpay, receive refunds, or have credit memos applied to their account.
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleCreateCredit}
                disabled={!userPermissions?.can_access_credits}
                sx={{ textTransform: 'none', fontWeight: 600 }}
              >
                Create Credit
              </Button>
            </Box>
          )}
        </Paper>

        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={handleMenuClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          PaperProps={{ sx: { minWidth: 180 } }}
        >
          <MenuItem
            onClick={handleApplyToInvoice}
            sx={{ gap: 1.5 }}
          >
            <ApplyIcon fontSize="small" /> Apply to Invoice
          </MenuItem>
          <MenuItem onClick={handleDeleteCredit} sx={{ gap: 1.5, color: 'error.main' }}>
            <DeleteIcon fontSize="small" /> Delete
          </MenuItem>
        </Menu>
    </Box>
  );
};

export default BillingCredits;
